/**
 * Share results — opens a sheet with a rendered preview card plus three
 * actions: (1) share via Telegram (opens the native chat picker via
 * ``t.me/share/url``), (2) copy the deep-link to the clipboard, (3) try to
 * post to Telegram Story when the runtime supports it.
 */

import { motion } from "framer-motion";
import { Copy, Send, Sparkles, Wand2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button, Sheet } from "@/design";
import { useShareCard } from "@/api/stats";
import { cn } from "@/lib/cn";
import { haptic, tg, BOT_USERNAME } from "@/lib/telegram";

interface Props {
  open: boolean;
  onClose: () => void;
  days: number;
}

export function ShareSheet({ open, onClose, days }: Props) {
  const { data: card, isLoading } = useShareCard(days, { enabled: open });
  const [copied, setCopied] = useState(false);

  const handleShareToTelegram = () => {
    if (!card) return;
    haptic.impact("medium");
    if (tg) {
      tg.openTelegramLink(card.share_url);
    } else {
      window.open(card.share_url, "_blank", "noopener");
    }
  };

  const handleCopy = async () => {
    if (!card) return;
    haptic.select();
    try {
      await navigator.clipboard.writeText(card.deep_link);
      setCopied(true);
      toast.success("Ссылка скопирована");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Не удалось скопировать");
    }
  };

  const handleShareToStory = () => {
    if (!card) return;
    haptic.impact("medium");
    if (tg?.shareToStory) {
      // Story sharing requires a media URL — pass deep link; on devices that
      // don't support story sharing we silently fall back to inline share.
      try {
        tg.shareToStory(card.deep_link, { text: card.lines.join("\n") });
      } catch {
        handleShareToTelegram();
      }
    } else {
      toast.message("Сторис недоступны на этом устройстве — используй обычную отправку");
      handleShareToTelegram();
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Поделиться результатами"
      description="Покажи свои цифры в чате или сторис"
    >
      <div className="space-y-5">
        {/* Preview card */}
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            "relative overflow-hidden rounded-3xl border border-white/[0.06]",
            "bg-gradient-to-br from-[#1A1A1F] via-[#15131A] to-[#0F0F12]",
            "p-6",
          )}
        >
          {/* Halo */}
          <div
            className="absolute -top-20 -right-20 size-64 rounded-full bg-accent/20 blur-3xl"
            aria-hidden
          />
          <div className="relative space-y-4">
            <div className="flex items-center gap-2">
              <div className="size-9 rounded-xl bg-gradient-accent grid place-items-center shadow-[0_8px_32px_-8px_rgba(255,69,0,0.6)]">
                <Sparkles className="size-4 text-white" />
              </div>
              <div>
                <div className="text-[10px] text-text-dim uppercase tracking-wider font-medium">
                  SilentPoster
                </div>
                <div className="text-sm font-semibold">
                  {card?.title ?? (isLoading ? "Загрузка…" : `За ${days} дней`)}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {(card?.lines ?? defaultLines(days)).map((line, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.25, delay: 0.05 + i * 0.05 }}
                  className="text-[17px] font-medium tabular-nums tracking-tight"
                >
                  {line}
                </motion.div>
              ))}
            </div>

            <div className="pt-2 border-t border-white/[0.06] text-[11px] text-text-muted">
              {card?.footer ?? `Powered by @${BOT_USERNAME}`}
            </div>
          </div>
        </motion.div>

        {/* Actions */}
        <div className="space-y-2">
          <Button
            fullWidth
            leftIcon={<Send />}
            onClick={handleShareToTelegram}
            disabled={!card}
          >
            Поделиться в Telegram
          </Button>
          <Button
            variant="secondary"
            fullWidth
            leftIcon={<Wand2 />}
            onClick={handleShareToStory}
            disabled={!card}
          >
            В историю
          </Button>
          <Button
            variant="ghost"
            fullWidth
            leftIcon={<Copy />}
            onClick={handleCopy}
            disabled={!card}
          >
            {copied ? "Скопировано ✓" : "Скопировать ссылку"}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

function defaultLines(days: number): string[] {
  return [
    `✅ 0 публикаций`,
    `💬 0 чатов`,
    `🟢 0 аккаунтов`,
    `За ${days} дней`,
  ];
}
